<script>
	import { onDestroy, onMount } from 'svelte';
	import { getApiUrl, getDivisionTitle, injectGlobalStyles, loadJson, normalizeCollection } from './shared.js';
	import WidgetSelect from './WidgetSelect.svelte';
	import RoundRobin from './RoundRobin.svelte';
	import Playoff from './Playoff.svelte';

	export let projectId = '';

	let host;
	let leagues = [];
	let selectedLeagueId = '';
	let selectedLeagueTag = '';
	let selectedStanding = null;
	let loadingLeagues = false;
	let loadingStanding = false;
	let errorMessage = '';
	let syncingFromGlobal = false;

	const allowedCompTypes = ['RoundRobin', 'Playoff'];
	const LEAGUE_CHANGED_EVENT = 'ilb:league-changed';

	$: leagueOptions = buildLeagueOptions(leagues);

	$: {
		if (selectedLeagueId && !loadingLeagues) {
			rememberLeagueId(selectedLeagueId);
			if (!syncingFromGlobal) {
				publishLeagueSelection(selectedLeagueId);
			} else {
				syncingFromGlobal = false;
			}
			loadStanding(selectedLeagueId);
		}
	}

	function getLeagueSelectionStore() {
		const globalScope = window;
		if (!globalScope.__ilbSelectedLeagueByProject) {
			globalScope.__ilbSelectedLeagueByProject = {};
		}
		return globalScope.__ilbSelectedLeagueByProject;
	}

	function getStoredLeagueId() {
		if (!projectId) return '';
		return getLeagueSelectionStore()[projectId] || '';
	}

	function rememberLeagueId(leagueId) {
		if (!projectId || !leagueId) return;
		getLeagueSelectionStore()[projectId] = leagueId;
	}

	function publishLeagueSelection(leagueId) {
		if (!projectId || !leagueId) return;
		window.dispatchEvent(new CustomEvent(LEAGUE_CHANGED_EVENT, {
			detail: { projectId, leagueId }
		}));
	}

	function handleGlobalLeagueChange(event) {
		const payload = event?.detail;
		if (!payload || payload.projectId !== projectId) return;
		if (!payload.leagueId || payload.leagueId === selectedLeagueId) return;
		if (!leagueOptions.some((item) => item.value === payload.leagueId)) return;
		syncingFromGlobal = true;
		selectedLeagueId = payload.leagueId;
	}

	function getCompType(standing) {
		return standing?.comp?.compType || standing?.comp?.CompType || '';
	}

	function isAllowedCompType(standing) {
		return allowedCompTypes.includes(getCompType(standing));
	}

	function getStandingTitle(standing) {
		return standing?.comp?.name || standing?.comp?.Name || standing?.competition?.title || standing?.competition?.Title || '';
	}

	function getLeagueId(league, index) {
		return league?.id || league?._id || league?.providerId || `league-${index}`;
	}

	function getLeagueTitle(league, index) {
		return getDivisionTitle(league, `Лига ${index + 1}`);
	}

	function getLeagueTag(league) {
		return league?.tag || league?.Tag || '';
	}

	function getSelectedLeague() {
		return leagues.find((league, index) => String(getLeagueId(league, index)) === String(selectedLeagueId)) || null;
	}

	function buildLeagueOptions(items) {
		return (items || []).map((league, index) => ({
			value: getLeagueId(league, index),
			label: getLeagueTitle(league, index)
		}));
	}

	async function loadLeagues() {
		if (!projectId) {
			leagues = [];
			selectedLeagueId = '';
			selectedStanding = null;
			return;
		}

		loadingLeagues = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/project/${encodeURIComponent(projectId)}/leagues`;
			leagues = normalizeCollection(await loadJson(url));
			const options = buildLeagueOptions(leagues);
			const storedLeagueId = getStoredLeagueId();

			if (storedLeagueId && options.some((item) => item.value === storedLeagueId)) {
				selectedLeagueId = storedLeagueId;
			} else if (!selectedLeagueId || !options.some((item) => item.value === selectedLeagueId)) {
				selectedLeagueId = options[0]?.value || '';
			}
		} catch (error) {
			console.error('Load Standings Leagues Error:', error);
			errorMessage = 'Ошибка загрузки лиг';
			leagues = [];
			selectedLeagueId = '';
			selectedStanding = null;
		} finally {
			loadingLeagues = false;
		}
	}

	async function loadStanding(leagueId) {
		if (!leagueId) {
			selectedStanding = null;
			return;
		}

		selectedLeagueTag = getLeagueTag(getSelectedLeague());
		loadingStanding = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/competition/${encodeURIComponent(leagueId)}/standings`;
			const standings = normalizeCollection(await loadJson(url)).filter(isAllowedCompType);
			selectedStanding = standings.find((item) => (item?.competition?.id || item?.competition?.Id) === leagueId) || standings[0] || null;
		} catch (error) {
			console.error('Load Standing Error:', error);
			errorMessage = 'Ошибка загрузки положения команд';
			selectedStanding = null;
		} finally {
			loadingStanding = false;
		}
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		window.addEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
		loadLeagues();
	});

	onDestroy(() => {
		window.removeEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
	});
</script>

<section class="standings" data-comp-id={projectId} bind:this={host}>
	<div class="container">
		<div class="headline">
			<h2 class="standings__title">Турнирная таблица</h2>
			<WidgetSelect
				className="--black"
				name="league"
				options={leagueOptions}
				bind:value={selectedLeagueId}
				disabled={loadingLeagues || loadingStanding}
			/>
		</div>

		{#if errorMessage}
			<div class="widget-status" style="color: red;">{errorMessage}</div>
		{:else if loadingLeagues || loadingStanding}
			<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
		{:else if !leagues.length}
			<div class="widget-status">Лиги не найдены</div>
		{:else if !selectedStanding}
			<div class="widget-status">Положение команд не найдено</div>
		{:else}
			{#if getStandingTitle(selectedStanding)}
				<h3 class="standings__subtitle">{getStandingTitle(selectedStanding)}</h3>
			{/if}

			{#if getCompType(selectedStanding) === 'RoundRobin'}
				<RoundRobin standings={selectedStanding.standings} tag={selectedLeagueTag} compId={selectedLeagueId} />
			{:else if getCompType(selectedStanding) === 'Playoff'}
				<Playoff
					playoffPairs={selectedStanding.playoffPairs}
					tag={selectedLeagueTag}
					compId={selectedStanding?.comp?.id}
				/>
			{:else}
				<div class="widget-status">Тип турнира не поддерживается</div>
			{/if}
		{/if}
	</div>
</section>

<style>
	:host {
		display: block;
	}

	.standings__subtitle {
		margin: 8px 0 12px;
		font-size: 16px;
		font-weight: 600;
	}
</style>
